import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from './Config';
import './LearningPage.css';

const LANGUAGES = [
  { code: 'original', api_code: 'EN', name: 'English' },
  { code: 'hindi', api_code: 'HI', name: 'Hindi' },
  { code: 'arabic', api_code: 'AR', name: 'Arabic' },
  { code: 'tamil', api_code: 'TA', name: 'Tamil' },
  { code: 'telugu', api_code: 'TE', name: 'Telugu' },
  { code: 'german', api_code: 'DE', name: 'German' },
  { code: 'italian', api_code: 'IT', name: 'Italian' },
  { code: 'portuguese', api_code: 'PT', name: 'Portuguese' },
  { code: 'russian', api_code: 'RU', name: 'Russian' }
];

function LearningPage() {
  const { projectId, masterProjectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [learningFolders, setLearningFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [description, setDescription] = useState('');

  useEffect(() => {
    fetchProject();
    if (projectId) {
      fetchLearningFolders();
    } else if (masterProjectId) {
      fetchLearningFoldersForMasterProject();
    }
  }, [projectId, masterProjectId]);

  const fetchProject = async () => {
    try {
      if (projectId) {
        // Fetch individual project
        const response = await fetch(`${API_URL}/projects/${projectId}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const projectData = await response.json();
        setProject(projectData);
        setDescription(projectData.description || '');
      } else if (masterProjectId) {
        // Fetch master project
        const response = await fetch(`${API_URL}/master-projects/${masterProjectId}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const masterProjectData = await response.json();
        setProject({
          id: masterProjectData.id,
          title: masterProjectData.name,
          description: masterProjectData.description || '',
          isMasterProject: true
        });
        setDescription(masterProjectData.description || '');
      }
    } catch (error) {
      console.error('Error fetching project/master project:', error);
    }
  };

  const fetchLearningFoldersForMasterProject = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/learning-folders/master-project/${masterProjectId}`);
      if (!response.ok) {
        if (response.status === 404) {
          setLearningFolders([]);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } else {
        const folders = await response.json();
        setLearningFolders(folders);
      }
    } catch (error) {
      console.error('Error fetching master project learning folders:', error);
      setLearningFolders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLearningFolders = async () => {
    if (!projectId) return;
    await fetchLearningFoldersForProject(projectId);
  };

  const fetchLearningFoldersForProject = async (targetProjectId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/learning-folders/${targetProjectId}`);
      if (!response.ok) {
        if (response.status === 404) {
          setLearningFolders([]);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } else {
        const folders = await response.json();
        setLearningFolders(folders);
      }
    } catch (error) {
      console.error('Error fetching learning folders:', error);
      setLearningFolders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateFolders = async () => {
    if (!description.trim()) {
      alert('Please enter a description before generating folders.');
      return;
    }

    setGenerating(true);
    try {
      let apiUrl;
      if (projectId) {
        apiUrl = `${API_URL}/learning-folders/${projectId}/generate`;
      } else if (masterProjectId) {
        apiUrl = `${API_URL}/learning-folders/master-project/${masterProjectId}/generate`;
      } else {
        alert('No project or master project found.');
        return;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: project.title,
          description: description.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      alert(`Learning folders generated successfully!\n\nCreated: ${result.created_folders.length} folders\nFailed: ${result.failed_folders.length} folders`);

      // Refresh the learning folders list
      if (projectId) {
        await fetchLearningFoldersForProject(projectId);
      } else if (masterProjectId) {
        await fetchLearningFoldersForMasterProject();
      }
      setShowGenerateModal(false);
    } catch (error) {
      console.error('Error generating folders:', error);
      alert(`Failed to generate folders: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const existingLanguages = new Set(learningFolders.map(folder => folder.language_code));
  const hasAllFolders = existingLanguages.size === LANGUAGES.length;

  const handleDeleteFolder = async (languageCode, languageName) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the learning folder for ${languageName}?\n\n` +
      `This action cannot be undone and will permanently remove the folder from both the database and external system.`
    );

    if (!confirmed) return;

    try {
      let apiUrl;
      if (projectId) {
        apiUrl = `${API_URL}/learning-folders/${projectId}/${languageCode}`;
      } else if (masterProjectId) {
        apiUrl = `${API_URL}/learning-folders/master-project/${masterProjectId}/${languageCode}`;
      } else {
        alert('No project or master project found.');
        return;
      }

      const response = await fetch(apiUrl, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      alert(`Successfully deleted learning folder for ${languageName}!`);

      // Refresh the learning folders list
      if (projectId) {
        await fetchLearningFoldersForProject(projectId);
      } else if (masterProjectId) {
        await fetchLearningFoldersForMasterProject();
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      alert(`Failed to delete folder for ${languageName}: ${error.message}`);
    }
  };

  return (
    <div className="learning-page">
      <div className="learning-header">
        <div className="header-left">
          <button onClick={() => navigate('/')} className="back-button">
            ← Back to Projects
          </button>
          <h1>Learning Folders</h1>
          {project && <h2>{project.title}</h2>}
        </div>

        {!hasAllFolders && (
          <button
            onClick={() => setShowGenerateModal(true)}
            className="generate-folders-button"
          >
            Generate Folders
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading learning folders...</p>
        </div>
      ) : (
        <div className="learning-folders-grid">
          {LANGUAGES.map(language => {
            const folder = learningFolders.find(f => f.language_code === language.code);
            return (
              <div key={language.code} className={`folder-card ${folder ? 'created' : 'not-created'}`}>
                <div className="folder-header">
                  <div className="language-info">
                    <h3>{language.name}</h3>
                    <span className="language-code">{language.api_code}</span>
                  </div>
                  <div className={`status-indicator ${folder ? 'created' : 'pending'}`}>
                    {folder ? '✓' : '○'}
                  </div>
                </div>

                {folder ? (
                  <div className="folder-details">
                    <div className="folder-header-actions">
                      <div className="folder-info">
                        <p className="folder-title">{folder.title}</p>
                        <p className="folder-description">{folder.description}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteFolder(language.code, language.name)}
                        className="delete-folder-button"
                        title={`Delete ${language.name} folder`}
                      >
                        🗑️
                      </button>
                    </div>
                    <div className="folder-meta">
                      <span className="external-id">ID: {folder.external_id}</span>
                      <span className="tag">{folder.tag}</span>
                    </div>
                    <div className="folder-dates">
                      <small>Created: {new Date(folder.created_at).toLocaleDateString()}</small>
                    </div>
                  </div>
                ) : (
                  <div className="folder-placeholder">
                    <p>Folder not created yet</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Generate Folders Modal */}
      {showGenerateModal && (
        <div className="modal-overlay" onClick={() => setShowGenerateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Generate Learning Folders</h3>
              <button onClick={() => setShowGenerateModal(false)} className="modal-close">×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Title (from {masterProjectId ? 'master project' : 'project'}):</label>
                <input
                  type="text"
                  value={project?.title || ''}
                  disabled
                  className="title-input disabled"
                />
              </div>

              <div className="form-group">
                <label>Description:</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter description for the learning folders..."
                  className="description-input"
                  rows="4"
                />
              </div>

              <div className="languages-preview">
                <p><strong>Will create folders for:</strong></p>
                <div className="languages-list">
                  {LANGUAGES.filter(lang => !existingLanguages.has(lang.code)).map(lang => (
                    <span key={lang.code} className="language-tag">{lang.name}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="cancel-button"
                disabled={generating}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateFolders}
                className="generate-button"
                disabled={generating || !description.trim()}
              >
                {generating ? 'Generating...' : 'Generate Folders'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LearningPage;